import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setUseBillingAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import setUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import CreateNewAddressModal, {
    CreateNewAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import EditExistingAddressModal, {
    EditExistingAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/EditExistingAddressModal";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { IconWrapper } from "@insite/mobius/Icon";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import ADIShipToSelector from "./ADIShipToSelector";
import ADIAddressInfoDisplay, { ADIAddressInfoDisplayStyles } from "./ADIAddressInfoDisplay";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import ADIColors from "@NA/Utils/ADIColors";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { createNewAddressModalStyles } from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";

interface OwnProps {
    address: ShipToModel | undefined;
    currentBillToId: string | undefined;
    newAddress?: ShipToModel;
    oneTimeAddress?: ShipToModel;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    onChange: (address: ShipToModel, typeOfAddress?: string) => void;
    showUseBillingAddress?: boolean;
    isUseBillingAddressDisabled?: boolean;
    onClickUseBillingAddress?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: ADIShippingAddressStyles;
    deliveryMethod?: string;
    cartType?: string;
    shipToObj?: any;
    billToObj?: any;
    allShiptos?: any;
    selectedAltAddress?: ShipToModel;
    isRadioDisabled: boolean;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = getCurrentCartState(state);
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const billToState = getBillToState(state, cart?.value?.billToId);
    const billToId = state.data.carts.byId?.current?.billToId;
    const shipToAddressFields = getAddressFieldsDataView(state).value?.shipToAddressFields;
    let shipTosArray;

    if (billToId) {
        const parameter: GetShipTosApiParameter = {
            billToId: billToId,
            expand: ["validation"],
            exclude: ["showAll", "billTo", "oneTime", "createNew"],
        };
        shipTosArray = getShipTosDataView(state, parameter).value;
    }

    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
        cart,
        billToState,
        shipToAddressFields,
        shipTosArray,
        isCartLoading: state.ADIGlobalData.ADIUpdateCartLoading.loading || cartState?.isLoading,
    };
};

const mapDispatchToProps = {
    setUseBillingAddress,
    setUseOneTimeAddress,
    loadShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ADIShippingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    createNewAddressModal?: CreateNewAddressModalStyles;
    editAddressModal?: EditExistingAddressModalStyles;
    oneTimeAddressGridItem?: GridItemProps;
    oneTimeAddressCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    oneTimeAddressCheckbox?: CheckboxPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: ADIAddressInfoDisplayStyles;
    useBillingAddressButton?: ButtonPresentationProps;
    alternateShipButtonGrid?: GridItemProps;
    alternateShipButtonGridPickUp?: GridItemProps;
    newAddressButton?: ButtonPresentationProps;
    selectAddressButton?: ButtonPresentationProps;
}

export const adiShippingAddressStyles: ADIShippingAddressStyles = {
    selectAddressButton: {
        css: css`
            max-width: 120px !important;
            padding: 5px;
            &:focus {
                outline: none;
            }
            &:disabled {
                border: 1px solid ${ADIColors.gray15} !important;
            }
        `,
    },
    newAddressButton: {
        variant: "secondary",
        css: css`
            &:focus {
                outline: none;
            }
            &:hover {
                background: ${ADIColors.secondaryButtonHoverLightBlue};
            }
            &:disabled {
                border: 1px solid ${ADIColors.gray15} !important;
                &:hover {
                    background: transparent;
                }
            }
        `,
    },
    alternateShipButtonGrid: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding: 15px 10px 0px 0px;
            margin-top: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 100%;
                        padding: 0;
                        margin-top: 20px !important;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        max-width: 319px;
                        width: 100%;
                    `,
                    css`
                        max-width: 319px;
                        width: 100%;
                    `,
                    null,
                ])}
            >${ButtonWrapper} {
                padding: 0px 10px;
                border-radius: 4px;
                flex: initial;
                max-width: initial !important;
                white-space: normal !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                &:nth-child(1) {
                    margin-right: 12px;
                    border: 1px solid ${getColor("secondary")};
                }
            }
        `,
    },
    alternateShipButtonGridPickUp: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 15px 10px 0px 0px;
            margin-top: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        max-width: 319px;
                        width: 100%;
                    `,
                    css`
                        max-width: 319px;
                        width: 100%;
                    `,
                    null,
                ])}
            >${ButtonWrapper} {
                padding: 0px 10px;
                border-radius: 4px;
                flex: initial;
                max-width: initial !important;
                white-space: normal !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100% !important;
                                min-width: 38% !important;
                            `,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                &:nth-child(1) {
                    margin-right: 12px;
                    border: 1px solid ${getColor("secondary")};
                }
            }
        `,
    },
    container: { gap: 20 },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    addressBookModal: {
        sizeVariant: "medium",
        cssOverrides: {
            modalContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 20px 9px;
                            `,
                            null,
                            css`
                                height: 75vh;
                                margin-top: 60px;
                                padding: 20px 9px;
                            `,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalBody: css`
                border-radius: 6px;
                border: 1px solid ${getColor("common.border")};
                padding: 28px;
                max-width: 606px !important;
                overflow-y: hidden;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding: 20px;
                                margin: 0px !important;
                            `,
                            css`
                                height: auto;
                                margin: 45px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
            modalTitle: css`
                border: none;
                padding: 0px;
                margin: 8px 0 20px 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                margin: 10px 0 17px;
                            `,
                            css`
                                margin: 10px 0 17px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
                > ${TypographyStyle} {
                    font-size: 28px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    font-size: 24px;
                                `,
                                css`
                                    font-size: 24px;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }
                > ${ButtonWrapper} {
                    margin-top: -30px;
                    margin-right: -10px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    padding: 0px;
                                    margin-top: -40px;
                                    min-height: 35px;
                                    margin-right: -10px;
                                `,
                                css`
                                    padding: 0px;
                                    margin-top: -40px;
                                    min-height: 40px;
                                    margin-right: -10px;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "max",
                        )}
                    &:hover {
                        background: transparent;
                        border-color: transparent;
                    }
                    &:focus {
                        outline: none;
                    }
                    > ${IconWrapper} {
                        color: ${getColor("primary")};
                        width: 35px;
                        height: 35px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        width: 35px;
                                        height: 35px;
                                    `,
                                    css`
                                        width: 35px;
                                        height: 35px;
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                        > svg {
                            width: 35px;
                            height: 35px;
                            stroke-width: 1.4px;
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        css`
                                            width: 35px;
                                            height: 35px;
                                            stroke-width: 1.2px;
                                        `,
                                        css`
                                            width: 35px;
                                            height: 35px;
                                            stroke-width: 1.2px;
                                        `,
                                        null,
                                        null,
                                        null,
                                    ],
                                    "max",
                                )}
                        }
                    }
                }
            `,
            modalContent: css`
                padding: 0px;
            `,
        },
    },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: { variant: "secondary" },
    oneTimeAddressGridItem: { width: 12 },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-left: 28px;
        `,
    },
    useBillingAddressButton: {
        variant: "secondary",
        css: css`
            margin-top: 20px;
        `,
    },
};

const ADIShippingAddress: FC<Props> = ({
    address,
    newAddress,
    onChange,
    useOneTimeAddress,
    extendedStyles,
    currentBillToId,
    shippingAddressFormState,
    deliveryMethod,
    shipTosArray,
    isCartLoading,
    isRadioDisabled,
}) => {
    const [newAddressModelOpen, setNewAddressModelOpen] = useState(false);
    const [selectAddressModalOpen, setSelectAddressModalOpen] = useState(false);
    const [editAddressModalOpen, setEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);

    const [styles] = useState(() => mergeToNew(adiShippingAddressStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);

    const newAddressClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setNewAddressModelOpen(true);
    };
    const newAddressModalCloseHandler = () => setNewAddressModelOpen(false);
    const newAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, "newAddress");
        setNewAddressModelOpen(false);
        toasterContext.addToast({
            messageType: "success",
            body: "Address has been created.",
        });
    };

    const selectAddressClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setSelectAddressModalOpen(true);
    };
    const selectAddressModalCloseHandler = () => setSelectAddressModalOpen(false);
    const selectAddressSelectHandler = (shipTo: ShipToModel) => {
        setSelectAddressModalOpen(false);
        onChange(shipTo);
    };

    const editAddressHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
        setEditAddressModalOpen(true);
    };
    const editAddressModalCloseHandler = () => {
        setEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };
    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address);
        setEditAddressModalOpen(false);
        setSelectAddressModalOpen(false);
        toasterContext.addToast({
            messageType: "success",
            body: "Address updated.",
        });
    };
    const [localAddress, setLocalAddress] = useState<ShipToModel | undefined>(undefined);

    // useEffect ----P21 Change
    useEffect(() => {
        setLocalAddress(address);
}, [address]);

    return (
        (
            <GridContainer {...styles.container} data-test-selector="checkoutShipping_shippingAddress">
                {address && shipTosArray && shipTosArray?.length > 2 && (
                    <GridItem {...styles.addressDisplayAndFormGridItem}>
                        <ADIAddressInfoDisplay
                            {...address}
                            state={
                                address.state
                                    ? address.state.abbreviation
                                        ? address.state.abbreviation
                                        : address.state.name
                                    : undefined
                            }
                            country={address.country ? address.country.abbreviation : undefined}
                            extendedStyles={styles.addressDisplay}
                        />
                    </GridItem>
                )}
                <GridItem
                    {...(deliveryMethod !== "pickup"
                        ? styles.alternateShipButtonGrid
                        : styles.alternateShipButtonGridPickUp)}
                >
                    <>
                        <Button disabled={isCartLoading || isRadioDisabled} {...styles.newAddressButton} onClick={newAddressClickHandler} 
                        css={css`
                                    width: 120px !important;
                                    padding: 15px 0px !important;
                            `}>
                            {translate("New Address")}
                        </Button>
                    </>
                    {shipTosArray && shipTosArray?.length > 2 && (
                        <Button
                            disabled={isCartLoading || isRadioDisabled}
                            {...styles.selectAddressButton}
                            onClick={selectAddressClickHandler}
                            css={css`
                                 width: 130px !important;
                                 padding: 6px !important;
                                 min-height: 48px !important;
                            `}
                        >
                            {translate("Select Address")}
                        </Button>
                    )}
                    <Modal
                        {...styles.addressBookModal}
                        headline={translate("Addresses on File")}
                        isOpen={selectAddressModalOpen}
                        handleClose={selectAddressModalCloseHandler}
                        data-test-selector="checkoutShipping_addressModal"
                    >
                        <ADIShipToSelector
                            currentShipTo={useOneTimeAddress && shippingAddressFormState ? undefined : address}
                            currentBillToId={currentBillToId}
                            onSelect={selectAddressSelectHandler}
                            onEdit={editAddressHandler}
                            onCreateNewAddressClick={newAddress && newAddressClickHandler}
                            extendedStyles={styles.shipToSelector}
                        />
                    </Modal>
                    {newAddress && (
                        <CreateNewAddressModal
                            newAddress={newAddress}
                            isModalOpen={newAddressModelOpen}
                            onModalClose={newAddressModalCloseHandler}
                            onFormSubmit={newAddressFormSubmitHandler}
                        />
                    )}
                    {addressBeingEdited && (
                        <EditExistingAddressModal
                            newAddress={addressBeingEdited}
                            isModalOpen={editAddressModalOpen}
                            onModalClose={editAddressModalCloseHandler}
                            onFormSubmit={editAddressFormSubmitHandler}
                        />
                    )}
                </GridItem>
            </GridContainer>
        )
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ADIShippingAddress);
